import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import reportWebVitals from "./reportWebVitals";
import "./style.css";
import smoothscroll from "smoothscroll-polyfill";
import { landingAssets } from "config/landing";
import { colorPalette, fontConfig } from "config/theme";

smoothscroll.polyfill();

const applyBrandingMetadata = () => {
  const { metadata } = landingAssets;
  if (!metadata) {
    return;
  }

  if (metadata.pageTitle) {
    document.title = metadata.pageTitle;
  }

  if (metadata.faviconHref) {
    const head = document.head;
    if (!head) return;

    const existingLink =
      Array.from(
        head.querySelectorAll<HTMLLinkElement>("link[rel='icon']")
      ).find((link) => !link.getAttribute("sizes")) ?? document.createElement("link");

    existingLink.rel = "icon";
    existingLink.href = metadata.faviconHref;

    if (!existingLink.parentElement) {
      head.appendChild(existingLink);
    }
  }
};

applyBrandingMetadata();

const applyFontBranding = () => {
  const root = document.documentElement;
  if (!root) return;

  if (fontConfig.body) {
    root.style.setProperty("--font-body", fontConfig.body);
    const body = document.body;
    if (body) {
      body.style.fontFamily = fontConfig.body;
    }
  }

  if (fontConfig.heading) {
    root.style.setProperty("--font-heading", fontConfig.heading);
  }
};

applyFontBranding();

const toCssVarName = (token: string) =>
  `--color-${token.replace(/([A-Z])/g, "-$1").toLowerCase()}`;

const normalizeColorValue = (value: string): string | null => {
  const trimmed = value.trim();
  const hexMatch = trimmed.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    const expand = (segment: string) =>
      segment.length === 1 ? segment.repeat(2) : segment;
    const normalized =
      hex.length === 3 || hex.length === 4
        ? hex
            .slice(0, 3)
            .split("")
            .map(expand)
            .join("")
        : hex.slice(0, 6);
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    if ([r, g, b].some((component) => Number.isNaN(component))) {
      return null;
    }
    return `${r} ${g} ${b}`;
  }
  const rgbMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbMatch) {
    const components = rgbMatch[1]
      .split(",")
      .map((part) => Number.parseFloat(part.trim()))
      .slice(0, 3);
    if (components.length === 3 && components.every((n) => !Number.isNaN(n))) {
      return components.join(" ");
    }
  }
  return null;
};

const applyColorBranding = () => {
  const root = document.documentElement;
  if (!root) return;
  Object.entries(colorPalette).forEach(([token, value]) => {
    if (!value) return;
    const cssVar = toCssVarName(token);
    const rgbValue = normalizeColorValue(value);
    if (cssVar && rgbValue) {
      root.style.setProperty(cssVar, rgbValue);
    }
  });
};

applyColorBranding();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(<App />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
